import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Archive, Copy, Megaphone, Calendar, DollarSign, Clock, Loader2 } from "lucide-react";

const statusColors = {
  DRAFT: "bg-gray-500",
  ACTIVE: "bg-green-500",
  PAUSED: "bg-yellow-500",
  COMPLETED: "bg-blue-500"
};

export default function ArchivedCampaigns() {
  const { token } = useAuth();
  const [archivedCampaigns, setArchivedCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copying, setCopying] = useState(null);

  useEffect(() => {
    fetchArchivedCampaigns();
  }, []);

  const fetchArchivedCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns/archived`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArchivedCampaigns(response.data);
    } catch (error) {
      toast.error("Failed to fetch archived campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async (campaignId) => {
    setCopying(campaignId);
    try {
      await axios.post(`${API}/campaigns/${campaignId}/copy`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Campaign copied to drafts! Check your active campaigns.");
    } catch (error) {
      toast.error("Failed to copy campaign");
    } finally {
      setCopying(null);
    }
  };

  const getStatusBadge = (status) => {
    const color = statusColors[status] || "bg-gray-500";
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        <span className={`w-2 h-2 rounded-full ${color}`}></span>
        {status || "Unknown"}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-pulse text-primary">Loading archived campaigns...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="archived-campaigns-page">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="p-3 rounded-xl bg-amber-500/10">
            <Archive className="h-7 w-7 text-amber-500" />
          </div>
          <div>
            <h1 className="text-4xl font-black tracking-tight">Archived Campaigns</h1>
            <p className="text-muted-foreground">
              View and copy archived campaigns • Archived campaigns cannot be restored
            </p>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Total Archived</span>
              <Badge className="bg-amber-500 text-white">{archivedCampaigns.length}</Badge>
            </div>
          </CardContent>
        </Card>
        {Object.entries(statusColors).map(([status, color]) => {
          const count = archivedCampaigns.filter(c => c.original_status === status).length;
          if (count === 0) return null;
          return (
            <Card key={status} className="bg-card border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Was {status}</span>
                  <Badge className={`${color} text-white`}>{count}</Badge>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Archived Campaigns List */}
      <Card className="bg-card border-border">
        <CardContent className="p-0">
          <div className="divide-y divide-border">
            {archivedCampaigns.map(campaign => (
              <div 
                key={campaign.id} 
                className="flex items-center justify-between p-4 hover:bg-muted/30 transition-colors"
                data-testid={`archived-campaign-${campaign.id}`}
              >
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="p-2.5 rounded-lg bg-amber-500/10">
                    <Megaphone className="h-5 w-5 text-amber-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold truncate">{campaign.name}</h3>
                      {getStatusBadge(campaign.original_status)}
                      <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-500/30">
                        <Archive className="h-3 w-3 mr-1" />
                        Archived
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {campaign.start_date ? new Date(campaign.start_date).toLocaleDateString('en-AU') : 'No date'} 
                        {campaign.end_date && ` - ${new Date(campaign.end_date).toLocaleDateString('en-AU')}`}
                      </span>
                      <span className="flex items-center gap-1">
                        <DollarSign className="h-3 w-3" />
                        ${campaign.budget?.toLocaleString() || 0} budget
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Archived {campaign.archived_at ? new Date(campaign.archived_at).toLocaleDateString('en-AU') : 'Unknown'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => handleCopy(campaign.id)}
                    disabled={copying === campaign.id}
                    data-testid={`copy-campaign-${campaign.id}`}
                  >
                    {copying === campaign.id ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Copying...
                      </>
                    ) : (
                      <>
                        <Copy className="mr-2 h-4 w-4" />
                        Copy to Drafts
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ))}
            {archivedCampaigns.length === 0 && (
              <div className="p-12 text-center">
                <Archive className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">No archived campaigns</h3>
                <p className="text-muted-foreground">
                  Archived campaigns will appear here. Archive campaigns from your active campaigns list.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
